import React, { useEffect, useMemo } from 'react';
import { Typography } from 'seagull';
import { useShallowStore, useStoreActions } from "../../../contexts";
import { selectors } from "../../../store/state-selectors";
import GraphWidgetLayout from "../GraphWidget/GraphWidgetLayout";
import GraphWidgetBottomCard from "../GraphWidget/GraphWidgetBottomCard";
import GraphWidgetSuccessState from "../GraphWidget/GraphWidgetSuccessState";
import VendorDashboardSeeWhatItsLike from "../VendorDashboardSeeWhatItsLike";
import AgreementPipelineTopCard from "./AgreementPipelineTopCard";
import AgreementPipelineChart from "./AgreementPipelineChart";
import styles from "../VendorDashboard.module.scss";
import { relationshipSummaryModel } from "../../../models";
import { RelationshipProgressStatus } from "../../../models/relationship";
import negate from 'lodash/negate';
const chartDataForLoading = [{
  type: 'sent',
  counts: {
    proposals: {
      ok: 5
    }
  }
}, {
  type: 'reviewed',
  counts: {
    proposals: {
      ok: 9
    }
  }
}, {
  type: 'paymentMethodConnected',
  counts: {
    proposals: {
      ok: 7
    }
  }
}, {
  type: 'microDepositInitiated',
  counts: {
    proposals: {
      ok: 2
    }
  }
}, {
  type: 'microDepositIssues',
  counts: {
    agreements: {
      warning: 0
    }
  }
}, {
  type: 'noPaymentMethod',
  counts: {
    agreements: {
      warning: 0
    }
  }
}];
const isProgressStatusMicroDepositInitiated = relationship => {
  const isMicroDepositInitiated = relationshipSummaryModel.progressStatus(relationship) === RelationshipProgressStatus.MicroDeposit;
  return isMicroDepositInitiated;
};
const isFundingSourceMicroDepositInitiated = relationship => {
  const fundingSource = relationshipSummaryModel.fundingSource(relationship);
  return !!fundingSource && fundingSource.isPendingMicroDeposit();
};
const isFundingSourceMicroDepositIssues = relationship => {
  const fundingSource = relationshipSummaryModel.fundingSource(relationship);
  return !!fundingSource && fundingSource.failedVerification();
};
const isWithoutPaymentMethod = relationship => {
  return relationshipSummaryModel.fundingSource(relationship) == null;
};
export default function AgreementPipelineWidget() {
  const businessId = useShallowStore(selectors.user.businessId);
  const [agreements, proposals] = useShallowStore(state => [state.agreements, state.proposals]);
  const {
    fetchAgreements,
    fetchProposals
  } = useStoreActions();
  useEffect(() => {
    if (businessId) {
      fetchAgreements();
      fetchProposals();
    }
  }, [businessId]);
  const data = useMemo(() => {
    if (!agreements.data || !proposals.data) {
      return null;
    }
    const proposalsWithoutSamples = proposals.data.filter(negate(relationshipSummaryModel.isSample));
    const agreementsWithoutSamples = agreements.data.filter(negate(relationshipSummaryModel.isSample));
    const sent = proposalsWithoutSamples.filter(p => p.progressStatus === RelationshipProgressStatus.Sent);
    const reviewed = proposalsWithoutSamples.filter(p => p.progressStatus === RelationshipProgressStatus.Reviewed);
    const paymentMethodConnected = proposalsWithoutSamples.filter(p => p.progressStatus === RelationshipProgressStatus.PaymentMethod);
    const mdInitiatedAgreements = agreementsWithoutSamples.filter(isFundingSourceMicroDepositInitiated).length;
    const mdInitiatedProposals = proposalsWithoutSamples.filter(isProgressStatusMicroDepositInitiated).length;
    const mdFailedAgreements = agreementsWithoutSamples.filter(isFundingSourceMicroDepositIssues).length;
    const noPaymentMethod = agreementsWithoutSamples.filter(isWithoutPaymentMethod).length;
    const ret = {
      isEmpty: agreementsWithoutSamples.length === 0 && proposalsWithoutSamples.length === 0,
      chart: [],
      totals: {
        pending: {
          count: proposalsWithoutSamples.length
        },
        approved: {
          count: agreementsWithoutSamples.length
        }
      }
    };
    if (sent.length) {
      ret.chart.push({
        type: 'sent',
        counts: {
          proposals: {
            ok: sent.length
          }
        }
      });
    }
    if (reviewed.length) {
      ret.chart.push({
        type: 'reviewed',
        counts: {
          proposals: {
            ok: reviewed.length
          }
        }
      });
    }
    if (paymentMethodConnected.length) {
      ret.chart.push({
        type: 'paymentMethodConnected',
        counts: {
          proposals: {
            ok: paymentMethodConnected.length
          }
        }
      });
    }
    if (mdInitiatedAgreements || mdInitiatedProposals) {
      ret.chart.push({
        type: 'microDepositInitiated',
        counts: {
          agreements: {
            ok: mdInitiatedAgreements
          },
          proposals: {
            ok: mdInitiatedProposals
          }
        }
      });
    }
    if (mdFailedAgreements) {
      ret.chart.push({
        type: 'microDepositIssues',
        counts: {
          agreements: {
            warning: mdFailedAgreements
          }
        }
      });
    }
    if (noPaymentMethod) {
      ret.chart.push({
        type: 'noPaymentMethod',
        counts: {
          agreements: {
            warning: noPaymentMethod
          }
        }
      });
    }
    return ret;
  }, [agreements.data, proposals.data]);
  const allApproved = data && data.totals.approved.count > 0 && data.totals.pending.count === 0 && data.chart.every(item => {
    var _item$counts$proposal;
    return (item.counts.proposals == null || ((_item$counts$proposal = item.counts.proposals) === null || _item$counts$proposal === void 0 ? void 0 : _item$counts$proposal.warning) == 0) && (item.counts.agreements == null || item.counts.agreements.warning === 0);
  });
  return /*#__PURE__*/React.createElement(GraphWidgetLayout, {
    title: "Agreements pipeline",
    loading: !!data && (agreements.loading || proposals.loading)
  }, /*#__PURE__*/React.createElement(AgreementPipelineTopCard, {
    title: "Pending proposals",
    primaryText: (data === null || data === void 0 ? void 0 : data.totals.pending.count) && "".concat(data === null || data === void 0 ? void 0 : data.totals.pending.count),
    testid: "agreement-pl-widget-card-proposals"
  }), /*#__PURE__*/React.createElement(AgreementPipelineTopCard, {
    title: "Approved agreements",
    primaryText: (data === null || data === void 0 ? void 0 : data.totals.approved.count) && "".concat(data === null || data === void 0 ? void 0 : data.totals.approved.count),
    testid: "agreement-pl-widget-card-agreements"
  }), /*#__PURE__*/React.createElement(GraphWidgetBottomCard, {
    loading: !data,
    loadingMsg: "Loading your agreements",
    empty: data === null || data === void 0 ? void 0 : data.isEmpty,
    emptyMsg: /*#__PURE__*/React.createElement("div", {
      className: styles.emptyStateWithAction
    }, /*#__PURE__*/React.createElement(Typography, null, "Here you will see your agreements"), /*#__PURE__*/React.createElement(VendorDashboardSeeWhatItsLike, {
      "data-testid": "agreements"
    })),
    loadingOrEmptyBg: () => /*#__PURE__*/React.createElement(AgreementPipelineChart, {
      chartData: chartDataForLoading
    }),
    error: (agreements.error || proposals.error) && !agreements.loading && !proposals.loading,
    "data-testid": "agreement-pl-widget-card-chart",
    onRefresh: () => {
      fetchAgreements();
      fetchProposals();
    }
  }, allApproved ? /*#__PURE__*/React.createElement(GraphWidgetSuccessState, {
    title: "Woohoo!",
    body: "All your agreements were approved",
    "data-testid": "agreement-pl-widget-success"
  }) : data && /*#__PURE__*/React.createElement(AgreementPipelineChart, {
    chartData: data.chart
  })));
}